import { Button } from 'antd';
import React, { FC, useContext, useEffect, useState } from 'react';
import { customFormatMessage, isFirstScan } from 'utils';
import ConsumableCoupon from './consumableCoupon';
import { ConsumablesContext } from './consumables';
import { refreshUserInfo } from 'src/actions/authActions';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { IRootReducers } from 'src/reducers';
import { IConsumableParams, ICouponParams } from 'src/shared/models';
import {
  ConsumableNatureEnum,
  ConsumablePointsOperatorEnum,
  CouponTypeEnum,
} from 'src/shared/enums/consumables.enum';
import * as api from 'services/api';
import classnames from 'classnames';

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ refreshUserInfo }, dispatch);

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    scanData: state.app.scanData,
  };
};

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ConsumablesModalBonusList: FC<Props> = ({
  scanData,
  refreshUserInfo,
}): JSX.Element => {
  const {
    setConsumablesStep,
    pointsSummaryStep,
    customerCoupons,
    setCustomerCoupons,
    selectedCustomerCoupon,
    setSelectedCustomerCoupon,
    setSkipCoupon,
    newCoupon,
  } = useContext(ConsumablesContext);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [orderedAvailableCoupons, setOrderedAvailableCoupons] = useState([]);

  const getCustomerCoupons = async () => {
    setOrderedAvailableCoupons(
      await orderCouponList(scanData.userConsumableCoupon),
    );
  };

  const orderCouponList = async (
    coupons: ICouponParams[],
  ): Promise<ICouponParams[]> => {
    if (coupons && coupons.length) {
      coupons.sort((a: ICouponParams, b: ICouponParams) => {
        if (getTotalCouponPoints(a) > getTotalCouponPoints(b)) return -1;
        if (getTotalCouponPoints(a) < getTotalCouponPoints(b)) return 1;
        if (a.expiration > b.expiration) return 1;
        if (a.expiration < b.expiration) return -1;
        return 0;
      });
    }
    return coupons;
  };

  const getTotalCouponPoints = (coupon: ICouponParams): number => {
    return coupon.pointsOperator === ConsumablePointsOperatorEnum.MULTIPLY
      ? coupon.points * coupon.consumableScanPoints
      : coupon.points;
  };

  useEffect(() => {
    getCustomerCoupons();
  }, []);

  useEffect(() => {
    setSelectedCustomerCoupon(orderedAvailableCoupons[0]);
    setCustomerCoupons(orderedAvailableCoupons);
  }, [orderedAvailableCoupons]);

  useEffect(() => {
    setSending(false);
    setLoading(false);
  }, [selectedCustomerCoupon]);

  const selectCoupon = (index: number) => {
    setSelectedCustomerCoupon(customerCoupons[index]);
  };

  const useCoupon = async () => {
    setDisabled(true);
    await api.consumeBonus(selectedCustomerCoupon, newCoupon);
    refreshUserInfo();
    setConsumablesStep(pointsSummaryStep);
    setDisabled(false);
  };

  const continueWithoutCoupon = () => {
    refreshUserInfo();
    setConsumablesStep(pointsSummaryStep);
    setSkipCoupon(true);
  };

  const couponListClasses: string = classnames(
    'consumables-modal-bonus-coupon coupon-cursor-pointer',
    {
      'single-coupon': customerCoupons.length === 1,
    },
  );

  const modalListClasses: string = classnames(
    'consumables-modal-bonus-list-step',
    {
      'consumables-modal-bonus-list-various': customerCoupons.length > 1,
    },
  );

  const mapCoupons = () => {
    return customerCoupons.map(
      (
        coupon: ICouponParams & { consumable?: IConsumableParams },
        index: number,
      ) => {
        const firstScan = isFirstScan(coupon);

        return (
          <div
            key={coupon.id}
            className={couponListClasses}
            onClick={() => selectCoupon(index)}>
            <ConsumableCoupon
              id={coupon.id}
              type={coupon.type}
              myCoupons={false}
              firstScan={firstScan}
              bonusPoints={coupon.points}
              consumablePoints={coupon.consumableScanPoints}
              operation={coupon.pointsOperator}
              consumableName={coupon.consumable.contents?.name}
              consumableImg={coupon.consumable.contents?.image}
              expiration={coupon.expiration}
              couponFromLanding={false}
              campaignType={coupon.campaignType}
              hasFamily={coupon.hasFamily}
            />
          </div>
        );
      },
    );
  };

  const renderListTitle = () => {
    if (customerCoupons.length === 1) {
      return customFormatMessage('consumable.modal.list.title');
    } else if (customerCoupons.length > 1) {
      return customFormatMessage('consumable.modal.list.title.several.cupons');
    }
  };

  const renderCouponList = () => {
    if (customerCoupons.length === 1) {
      return (
        <div className="consumables-modal-bonus-list__single">
          {mapCoupons()}
        </div>
      );
    } else if (customerCoupons.length > 1) {
      return <div className="consumables-modal-bonus-list">{mapCoupons()}</div>;
    }
  };

  return (
    <div className={modalListClasses}>
      <div className="consumables-modal-bonus-list-title">
        {renderListTitle()}
      </div>
      {renderCouponList()}
      <div className="consumables-modal-bonus-footer">
        <div className="consumables-modal-bonus-continue">
          <a
            onClick={continueWithoutCoupon}
            className="points-consumable-button-my-coupons">
            {customFormatMessage('consumable.modal.list.continue')}
          </a>
        </div>
        <div className="consumables-modal-bonus-button">
          <Button
            htmlType="submit"
            className="btn-primary btn-primary--black bonus-submit"
            onClick={useCoupon}
            block
            disabled={loading || disabled}
            loading={sending}>
            {customFormatMessage('consumable.modal.list.button')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConsumablesModalBonusList);
